import React, { forwardRef, useContext } from 'react';
import styled from 'styled-components';
import { StudioPrintSettingsContext, UserContext } from 'venn-components';
import { GetColor, getTextThemeProvider, ShimmerBlock, ZIndex } from 'venn-ui-kit';
import {
  STUDIO_DEFAULT_DISCLAIMER_FONT_SIZE,
  STUDIO_DEFAULT_USER_DISCLAIMERS,
  useHasFF,
  withSuspense,
} from 'venn-utils';
import {
  DEFAULT_MARGIN,
  selectedBlockIdState,
  studioCustomFooterText,
  studioHideFooterDisclaimer,
  studioHideFooterPagination,
} from 'venn-state';
import Measure from 'react-measure';
import { useRecoilValue } from 'recoil';
import {
  usePageFootNotesList,
  useRenderedPageFootNotesList,
} from '../../../../../venn-components/src/blocks/components/core';

interface PageFooterProps {
  /** Block ids present on this page — used to decide whether the selected block lives here and should sit above the footer. */
  pageBlockIds?: string[];
  totalPage: number;
  currentPage: number;
  setStudioFooterHeight?: (height: number) => void;
  useRenderedFootnotes?: boolean;
  /** Visual scale the footer is rendered under, so the measured height can be converted back to local px. */
  scale?: number;
}

const BOTTOM_OFFSET_PX = 10;

export const footerHeightToLocalPx = (measuredPx: number, scale: number, offsetPx: number): number =>
  measuredPx / (scale > 0 ? scale : 1) + offsetPx;

const DEFAULT_DISCLAIMERS = (organizationDisplayName?: string) => [
  `This report is provided to you by ${organizationDisplayName} for informational purposes only.  This report is prepared by ${organizationDisplayName} using ${
    getTextThemeProvider().appTitleFullName
  }, which is a tool that is designed to allow ${organizationDisplayName} to input certain metrics to produce this report. There is no guarantee that the performance and other metrics included herein are correct, complete, or accurate. This report is not investment advice or an offer to buy or sell a security, and should not be the basis for investment decision-making. Please see the back of this report for important information regarding the use of this report. Please contact ${organizationDisplayName} for further information.`,
];

const ViewPageFootNotes = withSuspense(
  <ShimmerBlock height={8} margin={1.2} />,
  ({ pageIndex }: { pageIndex: number }) => <>{usePageFootNotesList(pageIndex)}</>,
);

const RenderedPageFootNotes = withSuspense(
  <ShimmerBlock height={8} margin={1.2} />,
  ({ pageIndex }: { pageIndex: number }) => <>{useRenderedPageFootNotesList(pageIndex)}</>,
);

const PageFootNotes = ({ pageIndex, useRenderedFootnotes }: { pageIndex: number; useRenderedFootnotes: boolean }) =>
  useRenderedFootnotes ? <RenderedPageFootNotes pageIndex={pageIndex} /> : <ViewPageFootNotes pageIndex={pageIndex} />;

// TODO(VENN-24534): add a display name to this React component
// eslint-disable-next-line react/display-name
const PageFooter = forwardRef<HTMLDivElement, PageFooterProps>(
  ({ currentPage, pageBlockIds, totalPage, setStudioFooterHeight, useRenderedFootnotes, scale }, forwardedRef) => {
    const selectedBlockId = useRecoilValue(selectedBlockIdState);
    const isBlockSelected = !!selectedBlockId && !!pageBlockIds?.includes(selectedBlockId);
    const { profileSettings } = useContext(UserContext);
    const { organizationPrintSettings } = useContext(StudioPrintSettingsContext);
    const hideFooterDisclaimer = useRecoilValue(studioHideFooterDisclaimer);
    const hideFooterPagination = useRecoilValue(studioHideFooterPagination);
    const customFooterText = useRecoilValue(studioCustomFooterText);
    const pageIndex = currentPage - 1;

    const organizationDisplayName = organizationPrintSettings?.organizationName ?? profileSettings?.organization?.name;
    const organizationDefaultDisclaimer = STUDIO_DEFAULT_USER_DISCLAIMERS(organizationDisplayName);
    const disclaimerFontSize = organizationPrintSettings?.disclaimerFontSize ?? STUDIO_DEFAULT_DISCLAIMER_FONT_SIZE;

    const enableReporting = useHasFF('studio_report_editor');
    const disclaimer = !hideFooterDisclaimer
      ? (organizationPrintSettings?.disclaimer ?? organizationDefaultDisclaimer)
      : '';
    const defaultDisclaimers = DEFAULT_DISCLAIMERS(organizationDisplayName);

    const hidePageFooter =
      hideFooterPagination &&
      hideFooterDisclaimer &&
      !customFooterText &&
      !(enableReporting && disclaimer) &&
      (!defaultDisclaimers || defaultDisclaimers.length === 0);

    return (
      <Footer moveToBackground={isBlockSelected} ref={forwardedRef}>
        <Measure
          bounds
          onResize={(contentRect) => {
            contentRect?.bounds?.height &&
              contentRect.bounds.height > 0 &&
              setStudioFooterHeight?.(footerHeightToLocalPx(contentRect.bounds.height, scale ?? 1, BOTTOM_OFFSET_PX));
          }}
        >
          {({ measureRef }) => {
            return (
              <div ref={measureRef} data-testid="qa-page-footer">
                {!hidePageFooter && (
                  <>
                    <Copy fontSize={disclaimerFontSize}>
                      <PageFootNotes pageIndex={pageIndex} useRenderedFootnotes={!!useRenderedFootnotes} />
                      {enableReporting && (
                        /* eslint-disable-next-line react/no-danger */
                        <div dangerouslySetInnerHTML={{ __html: disclaimer }} className="[&_p]:m-0" />
                      )}
                      {defaultDisclaimers.map((message) => (
                        <div key={message}>{message}</div>
                      ))}
                    </Copy>
                    <FlexBetween>
                      <div>
                        {!hideFooterPagination && (
                          <>
                            Page: {currentPage} / {totalPage}
                          </>
                        )}
                      </div>
                      <div>{customFooterText}</div>
                    </FlexBetween>
                  </>
                )}
              </div>
            );
          }}
        </Measure>
      </Footer>
    );
  },
);

export default PageFooter;

const Footer = styled.div<{ moveToBackground?: boolean }>`
  position: absolute;
  bottom: ${BOTTOM_OFFSET_PX}px;
  left: 20px;
  width: calc(100% - ${DEFAULT_MARGIN * 2}px);
  z-index: ${({ moveToBackground }) => (moveToBackground ? ZIndex.Background : ZIndex.StickyFront)};
`;

const FlexBetween = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1.125rem;
`;

const Copy = styled.div<{ fontSize: number }>`
  white-space: pre-wrap;
  padding: 10px 0;
  border-top: 1px solid ${GetColor.GreyScale.Grey30};
  background: ${GetColor.White};
  overflow: hidden;
  font-size: ${({ fontSize }) => fontSize}pt;
  @media print {
    font-size: ${({ fontSize }) => fontSize}pt;
  }
`;
